import Image from 'next/image';
import { EventCard } from '@/sanity/fetchEvents';
import { EventDate } from '@/shared/types/eventData';
import { format, isSameDay } from 'date-fns';
import Chip from '../base/Chip';
import { generateGoogleMapsLink } from '@/utils/api-helpers';
import styles from './EventFacts.module.scss';

export const IconWithText = ({ icon, text }: { icon: string; text?: string }) => {
  if (!text) return null;
  return (
    <div className={styles.iconWithText}>
      <Image src={icon} width={18} height={18} alt="Icon" className={styles.icon} />
      <div className={styles.label}>{text}</div>
    </div>
  );
};

// Main component for event details icons
const EventFacts = ({
  event,
  currentDate = new Date(),
  today,
  className,
  locationOnClick,
  ticketLinkOnClick,
}: {
  event: EventCard;
  currentDate?: Date;
  today?: Date;
  className?: string;
  locationOnClick?: () => void;
  ticketLinkOnClick?: () => void;
}) => {
  const firstValidEventDate = getFirstValidEventDate(event, currentDate, today); // Assume this function is defined elsewhere
  const googleMapsLinks = generateGoogleMapsLink(event.location);

  return (
    <div className={className}>
      <div className={styles.chipList}>
        {firstValidEventDate && (
          <Chip label={firstValidEventDate} icon="/icons/icon-calendar.svg" />
        )}
        {event?.startTime && (
          <Chip
            label={`${event.startTime}  ${event.endTime ? ` - ${event.endTime}` : ''} Uhr`}
            icon="/icons/icon-clock.svg"
          />
        )}
        <div className={styles.chipListBreak} />
        {event?.location?.name && (
          <Chip
            label={event.location?.name}
            icon="/icons/icon-map.svg"
            href={googleMapsLinks}
            onClick={locationOnClick}
          />
        )}
        {(event?.ticketLink || event?.price) && (
          <Chip
            label={
              event.price
                ? event.ticketLink
                  ? `Ticket (${event.price})`
                  : `${event.price}`
                : 'Ticket'
            }
            icon="/icons/icon-ticket.svg"
            href={event.ticketLink}
            onClick={ticketLinkOnClick}
          />
        )}
      </div>
    </div>
  );
};

export default EventFacts;

// Defines a function to get the first valid event date or a formatted date message
export function getFirstValidEventDate(
  event: EventCard,
  date: Date,
  today: Date = date,
): string | undefined {
  const referenceDate = format(date, 'yyyy-MM-dd');
  const todayDate = format(today, 'yyyy-MM-dd');

  // Finds the first future or ongoing event date relative to the selected day
  const firstValidDate = event?.eventDates?.find(
    (eventDate) =>
      eventDate.startDate >= referenceDate ||
      (eventDate.endDate && eventDate.endDate >= referenceDate),
  );

  if (!firstValidDate) {
    // Handles case where no valid future or ongoing date is found
    return formatEventDateMessage(event?.eventDates[0], todayDate);
  }

  // Returns formatted date message for the valid date
  return formatEventDateMessage(firstValidDate, todayDate);
}

// Helper function to format a date range or single date into a string message
function formatEventDateMessage(date: EventDate, todayDate: string): string | undefined {
  if (!date) return undefined;

  const formattedStartDate =
    date.startDate === todayDate ? 'Heute' : format(new Date(date.startDate), 'dd.MM');

  if (date.endDate && !isSameDay(new Date(date.startDate), new Date(date.endDate))) {
    const formattedEndDate =
      date.endDate === todayDate ? 'Heute' : format(new Date(date.endDate), 'dd.MM');
    return `${formattedStartDate} - ${formattedEndDate}`;
  }

  return formattedStartDate;
}
